import { LoaderFunction } from 'react-router-dom';
import { ContentType } from '@atlassian/learning-components';

import { fetchGraphQL } from 'src/services/contentful/client';
import {
  ContentfulLearningPath,
  ContentfulLearningPathResponse,
  GET_LEARNING_PATH,
} from 'src/graphql/queries/GetLearningPath';
import { GetLearningPathQueryVariables } from 'src/graphql/generated/GetLearningPath';
import { getNotificationsById, NotificationsMappedResponse } from 'src/services/http/notifications';
import { mapLearningPathNotificationsData, sortNotificationsByDate } from 'src/utils/transformNotificationData';
import { isPreviewMode } from 'src/utils/isPreviewMode';

export const loader: LoaderFunction = async ({ params }): Promise<LearningPathLoaderProps> => {
  const pathId = params.pathId ?? '';
  const preview = isPreviewMode();

  const learningPath = await getLearningPath(pathId, preview);

  if (!learningPath) {
    throw new Error('Learning path was not found');
  }

  const learningPathNotifications = await getNotificationsById(learningPath?.sys.id, ContentType.LEARNING_PATH);
  const sortedUnseenNotifications = sortNotificationsByDate(learningPathNotifications?.unseenNotifications);
  const sortedHistoricalNotifications = sortNotificationsByDate(learningPathNotifications?.historicalNotifications);
  const mappedUnseenNotifications = mapLearningPathNotificationsData(sortedUnseenNotifications, learningPath);
  const mappedHistoricalNotifications = mapLearningPathNotificationsData(sortedHistoricalNotifications, learningPath);

  return {
    learningPath,
    notifications: {
      unseenNotifications: mappedUnseenNotifications,
      historicalNotifications: mappedHistoricalNotifications,
    },
  };
};

const getLearningPath = async (pathId: string, preview: boolean): Promise<ContentfulLearningPath | undefined> => {
  const response = await fetchGraphQL<ContentfulLearningPathResponse, GetLearningPathQueryVariables>(
    GET_LEARNING_PATH,
    { slug: pathId, preview },
  );

  return response.learningPathCollection.items[0];
};

export interface LearningPathLoaderProps {
  learningPath: ContentfulLearningPath;
  notifications: NotificationsMappedResponse;
}
